<template>
	<LayoutFlex
		padding="var(--size-12)"
		gap="var(--size-8)"
		background="--background-white"
		corner-radius="var(--size-36)"
	>
		<LayoutFlex padding="var(--size-16)" gap="var(--size-8)">
			<Text font="--text-14-20-med" color="--text-dark-primary">KOVA</Text>
			<Text font="--text-18-22-reg" color="--text-dark-primary">
				KOVA GLP-1
				<sup class="subscription-card__rx">Rx</sup>
				Guides
			</Text>
			<Text font="--text-14-20-med" color="--text-dark-secondary">
				KOVA gives you access to personalized GLP-1 treatment guided by licensed clinicians.
			</Text>
		</LayoutFlex>

		<LayoutFlex padding="0 var(--size-16)" gap="0" min-width="100%">
			<LayoutFlex
				v-for="row in detailsRows"
				:key="row.label"
				flex-direction="row"
				justify-content="space-between"
				align-items="center"
				padding="var(--size-16) 0"
				min-width="100%"
				:style="{ borderTop: '1px solid var(--stroke-dark-low)' }"
			>
				<Text font="--text-16-22-reg" color="--text-dark-primary">{{ row.label }}</Text>
				<RxStatusBadge v-if="row.rxStatus" :status="row.rxStatus" />
				<Text v-else font="--text-16-22-reg" color="--text-dark-primary">{{ row.value }}</Text>
			</LayoutFlex>
		</LayoutFlex>

		<LayoutFlex
			v-if="canManage"
			padding="var(--size-16)"
			:gap="paymentMethodLockup ? 'var(--size-16)' : '0'"
			background="--background-base"
			corner-radius="var(--size-24)"
			min-width="100%"
		>
			<LayoutFlex
				flex-direction="row"
				justify-content="space-between"
				align-items="center"
				padding="0"
				min-width="100%"
			>
				<Text font="--text-16-22-reg" color="--text-dark-primary">Payment method</Text>
				<button type="button" class="subscription-card__change-btn" @click="emit('change-payment-method')">
					<svg
						width="16"
						height="16"
						viewBox="0 0 15.1 15.4"
						fill="none"
						xmlns="http://www.w3.org/2000/svg"
						aria-hidden="true"
					>
						<path
							d="M2.84905 8.61253L9.67934 1.45586C10.4468 0.652004 11.7206 0.622284 12.5247 1.38947L13.4623 2.28432C14.3357 3.11786 14.3679 4.50143 13.5344 5.37486L13.1379 5.79025C12.8329 6.10977 12.3265 6.12166 12.0069 5.81665C11.6875 5.51161 11.6756 5.00515 11.9805 4.68558L12.3769 4.27019C12.6003 4.03601 12.5917 3.66522 12.3576 3.44177L11.42 2.54692C11.2551 2.38983 10.9942 2.39592 10.8368 2.56053L4.0065 9.7172C3.94808 9.77842 3.9036 9.85184 3.87613 9.93188L3.07525 12.2681C3.06792 12.2897 3.06991 12.2995 3.07112 12.3041C3.07329 12.3122 3.07962 12.3252 3.09234 12.3373C3.105 12.3493 3.1178 12.3546 3.12594 12.3564C3.1305 12.3574 3.14058 12.3597 3.16229 12.3511L5.45802 11.4416C5.53674 11.4105 5.60794 11.3626 5.66639 11.3014L9.58182 7.19887L9.11554 6.77653C8.78803 6.48003 8.76237 5.97427 9.05878 5.64672C9.35531 5.31918 9.86161 5.29398 10.1891 5.59051L10.9601 6.28869C11.4604 6.74182 11.4887 7.51833 11.0226 8.00665L6.82384 12.4061C6.60574 12.6346 6.34016 12.8135 6.04643 12.9298L3.75124 13.8388C2.42874 14.3622 1.10083 13.0946 1.56179 11.7492L2.36213 9.41352C2.46457 9.11469 2.63094 8.84106 2.84905 8.61253Z"
							fill="currentColor"
						/>
					</svg>
					Change
				</button>
			</LayoutFlex>
			<!-- Brand lockup of the current method (Figma nodes 30679-18302 / 30882-27753).
			     Masked digits were rejected by the owner; methods without a drawn lockup
			     (ideal / pix* / unknown / none) collapse the block to the single row. -->
			<img
				v-if="paymentMethodLockup"
				class="subscription-card__method-lockup"
				:src="paymentMethodLockup.src"
				:alt="paymentMethodLockup.alt"
				data-payment-method
			/>
		</LayoutFlex>

		<LayoutFlex v-if="canManage || lastBilledLabel" padding="var(--size-16) 0" gap="var(--size-16)">
			<Button v-if="canManage" hierarchy="secondary" full-width @ds:click="emit('manage')">
				<Text font="--text-16-18-semi">Manage subscription</Text>
			</Button>
			<Text v-if="lastBilledLabel" font="--text-14-20-med" color="--text-dark-secondary" text-align="center">
				{{ lastBilledLabel }}
			</Text>
		</LayoutFlex>
	</LayoutFlex>
</template>

<script setup lang="ts">
// The /status subscription card for every plan that is NOT a commitment — moved out of Status.vue
// unchanged when the commitment card got its own component (DEV-35727; see CommitmentCard.vue). Reads the subscription store itself; navigation and analytics stay on the
// page, which listens for `manage` / `change-payment-method`.
import { computed } from 'vue'
import { Button, LayoutFlex, Text } from '@simple-life-app/growth-ui-kova'
import RxStatusBadge from '@/components/RxStatusBadge.vue'
import type { RxStatus } from '@/api/medBilling'
import { useSubscriptionStore } from '@/store/subscription'
import { paymentMethodLockup as lockupFor } from '@/components/payment/paymentMethodLockups'

const props = defineProps<{
	/**
	 * `false` for a `cancelled` / `declined` plan (decided on the page): drops the affordances that
	 * imply an ongoing subscription — "Manage subscription", the Payment method block — and the
	 * "Charge Date" row, since there is no upcoming charge.
	 */
	canManage: boolean
}>()

const emit = defineEmits<{
	manage: []
	'change-payment-method': []
}>()

const store = useSubscriptionStore()

const statusLabel = computed(() => {
	const raw = store.subscription?.status
	if (raw) return humanizeStatus(raw)
	// Fallback if the backend omits `status`.
	const s = store.checkStatus
	if (s.isCancelled) return 'Cancelled'
	if (s.isPaused) return 'Paused'
	if (s.isOverdue) return 'Past due'
	if (s.isExpired) return 'Expired'
	if (s.isActive) return 'Active'
	return '—'
})

const billingCycleLabel = computed(() => {
	const phase = store.getActivePhase ?? store.getPhases.regular
	if (!phase?.interval_unit || !phase?.interval_duration) return '—'
	const noun = pluralize(phase.interval_unit, phase.interval_duration)
	return `${phase.interval_duration} ${noun}`
})

const amountLabel = computed(() => {
	const phase = store.getActivePhase ?? store.getPhases.regular
	const price = phase?.price
	const currency = phase?.currency ?? store.getCurrency?.toUpperCase?.() ?? 'USD'
	if (typeof price !== 'number') return '—'
	return formatMoney(price, currency)
})

// "Charge Date" = the next charge, i.e. the end of the current billing period (renewal) —
// not current_period_start_at (when the period began). Same field analytics sends as renewal_date.
const chargeDateLabel = computed(() => {
	const iso = store.subscription?.current_period_end_at
	return iso ? formatLongDate(iso) : '—'
})

const lastBilledLabel = computed(() => {
	const sub = store.subscription
	const amount = sub?.last_charge_amount
	const at = sub?.last_charge_at
	const phase = store.getActivePhase ?? store.getPhases.regular
	const currency = phase?.currency ?? 'USD'
	if (typeof amount !== 'number' || !at) return null
	return `Last billed ${formatMoney(amount, currency)} on ${formatLongDate(at)}`
})

// Brand lockup of the current method (shared with CommitmentCard — paymentMethodLockups.ts).
const paymentMethodLockup = computed(() => lockupFor(store.subscription?.payment_method))

// A row renders either a plain value or — for "Rx Status" — the coloured pill.
interface DetailsRow {
	label: string
	value?: string
	rxStatus?: RxStatus
}

const detailsRows = computed<DetailsRow[]>(() => {
	const rows: DetailsRow[] = [{ label: 'Status', value: statusLabel.value }]
	// "Rx Status" sits directly under "Status" per the design. Omitted entirely — no placeholder
	// dash — when `getRxStatus` is undefined: unknown Rx state, or a plan status outside
	// `active` / `awaiting_approval` (a cancelled plan must not still advertise a review).
	if (store.getRxStatus) rows.push({ label: 'Rx Status', rxStatus: store.getRxStatus })
	rows.push({ label: 'Billing Cycle', value: billingCycleLabel.value })
	rows.push({ label: 'Amount', value: amountLabel.value })
	// No upcoming charge for cancelled/declined subs → omit the "Charge Date" row.
	if (props.canManage) rows.push({ label: 'Charge Date', value: chargeDateLabel.value })
	return rows
})

// "awaiting_approval" → "Awaiting approval" (matches the "Past due" capitalization style).
function humanizeStatus(status: string): string {
	const spaced = status.replace(/_/g, ' ')
	return spaced.charAt(0).toUpperCase() + spaced.slice(1)
}

function pluralize(unit: string, count: number): string {
	const base = unit.endsWith('s') ? unit.slice(0, -1) : unit
	return count === 1 ? base : `${base}s`
}

// med-billing-engine returns prices in whole currency units (dollars): 449 → "$449.00".
function formatMoney(amount: number, currency: string): string {
	try {
		return new Intl.NumberFormat('en-US', { style: 'currency', currency }).format(amount)
	} catch {
		return `${currency} ${amount.toFixed(2)}`
	}
}

function formatLongDate(iso: string): string {
	const d = new Date(iso)
	if (Number.isNaN(d.getTime())) return iso
	return d.toLocaleDateString('en-US', { month: 'long', day: 'numeric', year: 'numeric' })
}
</script>

<style scoped>
/* The `Rx` superscript intrinsic to the product name string. */
.subscription-card__rx {
	font-size: 11.6px;
}

/* Keep the method lockup at its natural SVG size (the parent flex column stretches). */
.subscription-card__method-lockup {
	align-self: flex-start;
}

/* "Change" pill on the Payment method block. The purple accent comes from the Figma
   status tokens (status/purple) — the DS ships no purple tokens, so the values are local. */
.subscription-card__change-btn {
	appearance: none;
	display: inline-flex;
	align-items: center;
	gap: var(--size-4);
	height: 28px;
	padding: var(--size-4) var(--size-16);
	background: rgba(172, 100, 210, 0.2);
	color: #8a4aaa;
	border: none;
	border-radius: var(--size-16);
	font-family: 'Inter', sans-serif;
	font-size: 14px;
	font-weight: 500;
	line-height: 20px;
	cursor: pointer;
	transition: filter 0.15s ease;
}

.subscription-card__change-btn:hover {
	filter: brightness(0.96);
}

.subscription-card__change-btn:active {
	filter: brightness(0.92);
}
</style>
